import { api, types } from "api";
import {
	FormControl,
	FormHelperText,
	Grid,
	InputLabel,
	OutlinedInput,
	styled,
} from "@mui/material";
import { InfoSnackBar } from "components/InfoSnackBar";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import { StyledButton } from "components/StyledButton";
import { isValidEmail } from "helpers/authValidation";
import {
	EMAIL_POLICY_MSG,
	EMAIL_UPDATE_SUCCESS,
	EMAIL_UPDATE_FAIL,
} from "messages";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getPath, routes } from "routes";
import { useHistory, useParams } from "react-router-dom";

const PREFIX = "ChangeEmailPageEmailForm";

const classes = {
	root: `${PREFIX}-root`,
	container: `${PREFIX}-container`,
	form: `${PREFIX}-form`,
	baseButton: `${PREFIX}-baseButton`,
	textField: `${PREFIX}-textField`,
	inputLabel: `${PREFIX}-inputLabel`,
};

const Root = styled("form")(({ theme }) => ({
	[`& .${classes.container}`]: {
		flexGrow: 1,
		justifyContent: "flex-start",
		padding: theme.spacing(2),
	},

	[`&.${classes.form}`]: {
		flexGrow: 1,
		"& .MuiTextField-root": {
			margin: theme.spacing(1),
		},
	},

	[`& .${classes.baseButton}`]: {
		padding: "10px 25px",
		float: "left",
		margin: "0 0 0 20px",
	},

	[`& .${classes.textField}`]: {
		paddingBottom: "1em",
	},

	[`& .${classes.inputLabel}`]: {
		"&": {
			backgroundColor: theme.palette.background.paper,
		},
	},
}));

export const ChangeEmailPage = (): JSX.Element => {
	const [email, setEmail] = useState("");
	const [submit, setSubmit] = useState(false);
	const history = useHistory();
	const params = useParams<{ userId: types.UUID }>();
	const [user, setUser] = useState<types.User | null>(null);
	const [err, setErr] = useState(false);
	const { updateLastBreadcrumbs } = useBreadcrumbs();

	// Get the user id
	useEffect(() => {
		const query = params.userId
			? api.getUserDetails(params.userId)
			: api.getMyUserData();
		query
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr(false);
				setUser(res.data);

				if (params.userId) {
					updateLastBreadcrumbs([
						{
							name: `${res.data.firstName} ${res.data.lastName}`,
							params: { userId: params.userId },
						},
						undefined,
					]);
				}
			})
			.catch(() => {
				setErr(true);
			});
	}, [params.userId, updateLastBreadcrumbs]);

	const onSubmit = (event: React.FormEvent): void => {
		event.preventDefault();

		if (user?.userId === undefined) {
			setErr(true);
			setSubmit(true);
			return;
		}

		api
			.updateUserEmail(user.userId, { email })
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					setSubmit(true);
					return;
				}
				history.push(
					params.userId
						? getPath(routes._.people._.userProfile, { userId: params.userId })
						: routes._.people._.me.path,
				);
			})
			.catch(() => {
				setErr(true);
				setSubmit(true);
			});
	};
	return (
		<Root className={classes.root} onSubmit={onSubmit}>
			<InfoSnackBar
				isOpen={submit}
				onClose={() => setSubmit(false)}
				message={err ? EMAIL_UPDATE_FAIL : EMAIL_UPDATE_SUCCESS}
				severity={err ? "error" : "success"}
			/>
			<Grid
				container
				justifyContent="flex-start"
				direction="column"
				className={classes.container}
			>
				<FormHelperText>{EMAIL_POLICY_MSG}</FormHelperText>
				<br />
				<FormControl variant="filled" className={classes.textField}>
					<InputLabel className={classes.inputLabel}>New Email</InputLabel>
					<OutlinedInput
						required
						id="outlined-adornment-email-1"
						fullWidth
						error={!isValidEmail(email)}
						onChange={(e) => setEmail(e.target.value)}
						value={email}
						inputProps={{ "data-testid": "new-email" }}
						data-testid="outlined-adornment-email-1"
					/>
				</FormControl>

				<Grid container justifyContent="flex-end" alignItems="center">
					<Grid item>
						<StyledButton
							variant="contained"
							className={classes.baseButton}
							component={Link}
							to={
								params.userId
									? getPath(routes._.people._.userProfile, {
											userId: params.userId,
									  })
									: routes._.people._.me.path
							}
							data-testid="cancel-password-change"
						>
							Cancel
						</StyledButton>
					</Grid>
					<Grid item>
						<StyledButton
							variant="contained"
							color="primary"
							className={classes.baseButton}
							type="submit"
							data-testid="submit-password-change"
						>
							Change Email
						</StyledButton>
					</Grid>
				</Grid>
			</Grid>
		</Root>
	);
};
